import {
  collection, doc, getDoc, getDocs, limit, query, runTransaction, serverTimestamp, where,
} from 'firebase/firestore';
import { auth, db } from '../../config/firebase';

const safeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const hash = (value) => {
  let h = 2166136261;
  for (const ch of String(value || '')) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619); }
  return (h >>> 0).toString(36);
};
const normalizeReference = (value) => String(value || '').trim().replace(/\s+/g, ' ');
const normalizeStatus = (value) => {
  const raw = String(value || '').toLowerCase();
  if (['pending','pending_verification','paid','approved','rejected','failed','cancelled','refunded'].includes(raw)) return raw;
  return 'pending_verification';
};
const isApprovedStatus = (status) => ['paid','approved'].includes(String(status || '').toLowerCase());

const normalizeRole = (value) => String(value || '').toLowerCase().replace(/[\s-]+/g, '_');
const requireSuperAdmin = async () => {
  const current = auth.currentUser;
  if (!current?.uid) throw new Error('Super Admin authentication is required.');
  const profileSnap = await getDoc(doc(db, 'users', current.uid));
  if (!profileSnap.exists()) throw new Error('Super Admin profile was not found.');
  const profile = profileSnap.data() || {};
  if (!['super_admin', 'superadmin'].includes(normalizeRole(profile.role || profile.userRole)) || profile.isActive === false || profile.isBlocked === true) {
    throw new Error('Only an active Super Admin can verify payments.');
  }
  return current.uid;
};

const toDate = (value) => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (typeof value?._seconds === 'number') return new Date(value._seconds * 1000);
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const calculateSubscriptionEnd = (billingCycle, preferredStart, explicitEnd = '') => {
  const explicit = toDate(explicitEnd);
  if (explicit) return explicit;
  const cycle = String(billingCycle || '').toLowerCase().replace(/[ _-]+/g, '');
  if (['lifetime','forever','onetime','oneoff'].includes(cycle)) return null;
  const start = toDate(preferredStart) || new Date();
  const end = new Date(start.getTime());
  if (['annual','annually','year','yearly','12months'].includes(cycle)) end.setFullYear(end.getFullYear() + 1);
  else if (['quarter','quarterly','3months'].includes(cycle)) end.setMonth(end.getMonth() + 3);
  else if (['semiannual','semiannually','6months'].includes(cycle)) end.setMonth(end.getMonth() + 6);
  else if (['weekly','week'].includes(cycle)) end.setDate(end.getDate() + 7);
  else end.setMonth(end.getMonth() + 1);
  return end;
};

export async function listManualPayments() {
  await requireSuperAdmin();
  const snap = await getDocs(query(collection(db, 'paymentProofs'), where('source', '==', 'manual'), limit(500)));
  return snap.docs.map((row) => ({ id: row.id, ...row.data() }));
}

const applyPaidBusinessState = async (transaction, paymentRef, payment, companySnap, userSnap, planSnap = null) => {
  if (payment.businessStateApplied === true) return;
  const companyRef = payment.companyId ? doc(db, 'companies', payment.companyId) : null;
  const userRef = payment.userId ? doc(db, 'users', payment.userId) : null;
  const plan = planSnap?.exists?.() ? planSnap.data() || {} : {};
  const effectivePackageName = plan.name || payment.packageName || '';
  const effectiveBillingCycle = plan.billingCycle || payment.billingCycle || '';
  const effectiveCurrency = String(plan.currency || payment.currency || 'EUR').toUpperCase();
  const effectiveAmount = safeNumber(payment.amount);
  const baseCompany = {
    paymentStatus: 'paid',
    paymentAmount: effectiveAmount,
    paymentCurrency: String(payment.currency || effectiveCurrency || 'EUR').toUpperCase(),
    lastPaymentAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  if (companyRef && companySnap?.exists()) {
    if (payment.paymentType === 'company_registration') {
      const subscriptionPatch = {
        ...(companySnap.data()?.subscription || {}),
        status: 'active',
        manualPaymentId: paymentRef.id,
        lastPaymentAt: serverTimestamp(),
        pendingPaymentId: '',
        pendingPaymentStatus: '',
      };
      if (payment.packageId) subscriptionPatch.packageId = payment.packageId;
      if (effectivePackageName) subscriptionPatch.packageName = effectivePackageName;
      if (effectiveBillingCycle) subscriptionPatch.billingCycle = effectiveBillingCycle;
      if (plan.userLimit !== undefined) subscriptionPatch.userLimit = Number(plan.userLimit || 0);
      if (plan.maxUsers !== undefined) subscriptionPatch.maxUsers = Number(plan.maxUsers || 0);
      if (plan.unlimitedUsers !== undefined) subscriptionPatch.unlimitedUsers = Boolean(plan.unlimitedUsers);
      if (Array.isArray(plan.features)) subscriptionPatch.features = plan.features;
      if (plan.price !== undefined) subscriptionPatch.price = safeNumber(plan.price);
      subscriptionPatch.currency = effectiveCurrency;
      const registrationEnd = calculateSubscriptionEnd(effectiveBillingCycle, new Date(), payment.periodEnd);
      subscriptionPatch.startDate = serverTimestamp();
      if (registrationEnd) {
        subscriptionPatch.expiryDate = registrationEnd;
        subscriptionPatch.expiresAt = registrationEnd;
        subscriptionPatch.validUntil = registrationEnd;
      } else if (String(effectiveBillingCycle || '').toLowerCase() === 'lifetime') {
        subscriptionPatch.unlimitedDuration = true;
      }
      transaction.set(companyRef, {
        ...baseCompany,
        isActive: true,
        isBlocked: false,
        subscription: subscriptionPatch,
        registrationPaymentStatus: 'paid',
        registrationPaymentId: paymentRef.id,
      }, { merge: true });
    } else if (payment.paymentType === 'subscription' || payment.paymentType === 'subscription_renewal' || payment.paymentType === 'upgrade') {
      const subscriptionPatch = {
        ...(companySnap.data()?.subscription || {}),
        status: 'active',
        manualPaymentId: paymentRef.id,
        lastPaymentAt: serverTimestamp(),
        pendingPaymentId: '',
        pendingPaymentStatus: '',
      };
      if (payment.packageId) subscriptionPatch.packageId = payment.packageId;
      if (effectivePackageName) subscriptionPatch.packageName = effectivePackageName;
      if (effectiveBillingCycle) subscriptionPatch.billingCycle = effectiveBillingCycle;
      if (plan.userLimit !== undefined) subscriptionPatch.userLimit = Number(plan.userLimit || 0);
      if (plan.maxUsers !== undefined) subscriptionPatch.maxUsers = Number(plan.maxUsers || 0);
      if (plan.unlimitedUsers !== undefined) subscriptionPatch.unlimitedUsers = Boolean(plan.unlimitedUsers);
      if (Array.isArray(plan.features)) subscriptionPatch.features = plan.features;
      if (plan.price !== undefined) subscriptionPatch.price = safeNumber(plan.price);
      subscriptionPatch.currency = effectiveCurrency;
      const existingEnd = companySnap.data()?.subscription?.expiryDate || companySnap.data()?.subscription?.expiresAt || companySnap.data()?.subscription?.validUntil;
      const renewalBase = payment.paymentType === 'subscription_renewal' && toDate(existingEnd) && toDate(existingEnd) > new Date() ? toDate(existingEnd) : new Date();
      const subscriptionEnd = calculateSubscriptionEnd(effectiveBillingCycle, renewalBase, payment.periodEnd);
      subscriptionPatch.startDate = companySnap.data()?.subscription?.startDate || serverTimestamp();
      if (subscriptionEnd) {
        subscriptionPatch.expiryDate = subscriptionEnd;
        subscriptionPatch.expiresAt = subscriptionEnd;
        subscriptionPatch.validUntil = subscriptionEnd;
        subscriptionPatch.unlimitedDuration = false;
      } else if (String(effectiveBillingCycle || '').toLowerCase() === 'lifetime') {
        subscriptionPatch.unlimitedDuration = true;
      }
      transaction.set(companyRef, { ...baseCompany, subscription: subscriptionPatch }, { merge: true });
    } else if (payment.paymentType === 'extra_seat') {
      transaction.set(companyRef, { ...baseCompany, lastExtraSeatPaymentAt: serverTimestamp() }, { merge: true });
      if (userRef) {
        const approved = Boolean(userSnap?.data()?.isApproved);
        transaction.set(doc(db, 'companies', payment.companyId, 'extraSeatPayments', payment.userId), {
          userId: payment.userId,
          amount: safeNumber(payment.amount),
          currency: payment.currency || 'EUR',
          transactionReference: payment.transactionReference,
          status: 'paid',
          paymentProofId: paymentRef.id,
          reviewedBy: auth.currentUser?.uid || '',
          reviewedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }, { merge: true });
        transaction.set(userRef, {
          paymentStatus: 'extra_seat_paid',
          seatStatus: approved ? 'active' : 'reserved',
          isActive: approved,
          registrationStatus: approved ? 'active' : 'pending_company_approval',
          registrationIncomplete: !approved,
          registrationStage: approved ? 'completed' : 'company_approval',
          updatedAt: serverTimestamp(),
        }, { merge: true });
      }
    } else {
      transaction.set(companyRef, baseCompany, { merge: true });
    }
  }

  if (userRef && userSnap?.exists() && payment.paymentType === 'company_registration') {
    const profileCompleted = userSnap.data()?.profileCompleted === true;
    transaction.set(userRef, {
      paymentStatus: 'paid',
      registrationPaymentStatus: 'paid',
      registrationPaymentId: paymentRef.id,
      isActive: profileCompleted,
      registrationIncomplete: !profileCompleted,
      registrationStatus: profileCompleted ? 'active' : 'company_profile_finalization',
      registrationStage: profileCompleted ? 'completed' : 'company_profile_finalization',
      stage: profileCompleted ? 'completed' : 'company_profile_finalization',
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }

  transaction.set(paymentRef, { businessStateApplied: true, businessStateAppliedAt: serverTimestamp() }, { merge: true });
};

export async function recordManualPayment(input = {}) {
  await requireSuperAdmin();
  const reference = normalizeReference(input.transactionReference);
  if (!reference) throw new Error('Transaction/reference number is required.');
  const amount = safeNumber(input.amount);
  if (amount <= 0) throw new Error('Payment amount must be greater than zero.');
  if (!input.companyId && !input.userId) throw new Error('Choose a company or user for this payment.');
  const paymentType = String(input.paymentType || 'custom');
  if (paymentType === 'extra_seat' && (!input.companyId || !input.userId)) {
    throw new Error('Extra-seat payments require both a company and a user.');
  }
  if (['subscription', 'subscription_renewal', 'upgrade'].includes(paymentType) && !input.companyId) {
    throw new Error('Subscription payments require a company.');
  }
  const paymentId = `manual_${hash(`${input.companyId || input.userId || 'platform'}:${String(input.paymentMethod || 'bank_transfer').toLowerCase()}:${reference.toLowerCase()}`)}`;
  const paymentRef = doc(db, 'paymentProofs', paymentId);
  const status = normalizeStatus(input.status);

  await runTransaction(db, async (transaction) => {
    const existing = await transaction.get(paymentRef);
    if (existing.exists()) throw new Error('A payment with this transaction/reference number already exists.');
    const companyRef = input.companyId ? doc(db, 'companies', input.companyId) : null;
    const userRef = input.userId ? doc(db, 'users', input.userId) : null;
    const planRef = input.packageId ? doc(db, 'subscriptionPlans', input.packageId) : null;
    const companySnap = companyRef ? await transaction.get(companyRef) : null;
    const userSnap = userRef ? await transaction.get(userRef) : null;
    const planSnap = planRef ? await transaction.get(planRef) : null;
    if (companyRef && !companySnap.exists()) throw new Error('Selected company was not found.');
    if (userRef && !userSnap.exists()) throw new Error('Selected user was not found.');
    if (planRef && !planSnap.exists()) throw new Error('Selected subscription plan was not found.');

    const payment = {
      source: 'manual',
      paymentType,
      companyId: input.companyId || '',
      companyName: input.companyName || companySnap?.data()?.companyName || companySnap?.data()?.name || '',
      userId: input.userId || '',
      userName: input.userName || userSnap?.data()?.displayName || userSnap?.data()?.email || '',
      packageId: input.packageId || '',
      packageName: input.packageName || '',
      amount,
      currency: String(input.currency || 'EUR').toUpperCase(),
      paymentMethod: String(input.paymentMethod || 'bank_transfer'),
      transactionReference: reference,
      paymentDate: input.paymentDate || new Date().toISOString(),
      periodEnd: input.periodEnd || '',
      billingCycle: input.billingCycle || '',
      bankDetails: String(input.bankDetails || '').trim(),
      notes: String(input.notes || '').trim(),
      proofUrl: String(input.proofUrl || '').trim(),
      status,
      reviewStatus: isApprovedStatus(status) ? 'approved' : status === 'rejected' ? 'rejected' : 'pending',
      rejectionReason: '',
      email: String(input.email || userSnap?.data()?.email || companySnap?.data()?.adminEmail || '').trim().toLowerCase(),
      recordedBy: auth.currentUser?.uid || '',
      recordedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      businessStateApplied: false,
    };
    transaction.set(paymentRef, payment);
    transaction.set(doc(db, 'auditLogs', `payment_${paymentId}_${Date.now()}`), {
      module: 'payments', action: 'manual_payment_created', entityType: 'payment', entityId: paymentId,
      actorId: auth.currentUser?.uid || '', companyId: input.companyId || '', userId: input.userId || '',
      amount, currency: payment.currency, transactionReference: reference, createdAt: serverTimestamp(),
    });
    if (isApprovedStatus(status)) await applyPaidBusinessState(transaction, paymentRef, payment, companySnap, userSnap, planSnap);
  });
  return paymentId;
}

export async function updateManualPaymentStatus(paymentId, nextStatus, options = {}) {
  await requireSuperAdmin();
  const status = normalizeStatus(nextStatus);
  const rejectionReason = String(options.rejectionReason || '').trim();
  if (status === 'rejected' && rejectionReason.length < 3) throw new Error('A rejection reason is required.');
  const paymentRef = doc(db, 'paymentProofs', paymentId);
  return runTransaction(db, async (transaction) => {
    const paymentSnap = await transaction.get(paymentRef);
    if (!paymentSnap.exists()) throw new Error('Payment record was not found.');
    const payment = { id: paymentSnap.id, ...paymentSnap.data() };
    if (payment.source !== 'manual') throw new Error('Only manual payments can be updated here.');
    const companyRef = payment.companyId ? doc(db, 'companies', payment.companyId) : null;
    const userRef = payment.userId ? doc(db, 'users', payment.userId) : null;
    const planRef = payment.packageId ? doc(db, 'subscriptionPlans', payment.packageId) : null;
    const companySnap = companyRef ? await transaction.get(companyRef) : null;
    const userSnap = userRef ? await transaction.get(userRef) : null;
    const planSnap = planRef ? await transaction.get(planRef) : null;
    if (planRef && !planSnap.exists()) throw new Error('The subscription plan attached to this payment no longer exists.');
    if (isApprovedStatus(payment.status) && isApprovedStatus(status) && payment.businessStateApplied === true) {
      return { alreadyApproved: true, receiptNumber: payment.receiptNumber || options.receiptNumber || '' };
    }
    if (isApprovedStatus(payment.status) && !isApprovedStatus(status)) {
      throw new Error('An approved payment cannot be changed to another status. Use a separate refund/reversal workflow instead.');
    }
    const receiptNumber = options.receiptNumber || payment.receiptNumber || '';
    transaction.set(paymentRef, {
      status,
      reviewStatus: isApprovedStatus(status) ? 'approved' : status === 'rejected' ? 'rejected' : 'pending',
      rejectionReason: status === 'rejected' ? rejectionReason : '',
      receiptNumber: receiptNumber || payment.receiptNumber || '',
      updatedAt: serverTimestamp(), reviewedBy: auth.currentUser?.uid || '', reviewedAt: serverTimestamp(),
      ...(isApprovedStatus(status) ? { approvedAt: serverTimestamp(), approvedBy: auth.currentUser?.uid || '' } : {}),
    }, { merge: true });
    transaction.set(doc(db, 'auditLogs', `payment_status_${paymentId}_${Date.now()}`), {
      module: 'payments', action: `manual_payment_${status}`, entityType: 'payment', entityId: paymentId,
      actorId: auth.currentUser?.uid || '', previousStatus: payment.status || '', nextStatus: status, createdAt: serverTimestamp(),
    });
    if (isApprovedStatus(status) && payment.businessStateApplied !== true) await applyPaidBusinessState(transaction, paymentRef, { ...payment, status, receiptNumber: options.receiptNumber || payment.receiptNumber || '' }, companySnap, userSnap, planSnap);
    if (status === 'rejected') {
      if (companyRef && companySnap?.exists()) {
        if (payment.paymentType === 'company_registration') {
          transaction.set(companyRef, {
            paymentStatus: 'rejected', currentPaymentProofId: paymentRef.id, registrationPaymentStatus: 'rejected',
            subscription: {
              ...(companySnap.data()?.subscription || {}), status: 'pending_payment_rejected',
              pendingPaymentId: paymentRef.id, pendingPaymentStatus: 'rejected',
            },
            updatedAt: serverTimestamp(),
          }, { merge: true });
        } else {
          transaction.set(companyRef, {
            currentPaymentProofId: paymentRef.id,
            subscription: {
              ...(companySnap.data()?.subscription || {}), pendingPaymentId: paymentRef.id, pendingPaymentStatus: 'rejected',
            },
            updatedAt: serverTimestamp(),
          }, { merge: true });
        }
      }
      if (userRef && userSnap?.exists() && payment.paymentType === 'company_registration') transaction.set(userRef, { paymentStatus: 'company_payment_rejected', registrationPaymentStatus: 'rejected', registrationStatus: 'pending_company_subscription', registrationStage: 'company_subscription', stage: 'company_subscription', registrationIncomplete: true, updatedAt: serverTimestamp() }, { merge: true });
    }
    return { alreadyApproved: false, status, receiptNumber: options.receiptNumber || payment.receiptNumber || '' };
  });
}

export async function markPaymentReceiptEmail(paymentId, { sent, error = '' } = {}) {
  await requireSuperAdmin();
  const paymentRef = doc(db, 'paymentProofs', paymentId);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(paymentRef);
    if (!snap.exists()) throw new Error('Payment record was not found.');
    transaction.set(paymentRef, { receiptEmailSent: Boolean(sent), receiptEmailSentAt: sent ? serverTimestamp() : null, receiptEmailError: sent ? '' : String(error || '').slice(0, 1000), updatedAt: serverTimestamp() }, { merge: true });
  });
}
